import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { Reveal } from "./Reveal";
import { CONTACT } from "./data";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Phone, Mail, MapPin, ArrowRight, Loader2, CheckCircle2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const SERVICE_OPTIONS = ["Residential cleaning", "Commercial / office cleaning", "End of lease clean", "Carpet cleaning", "Deep clean", "Vehicle detailing"];
const EMPTY = { name: "", email: "", phone: "", service: "", message: "" };
const PROMISES = ["No hidden fees", "Fully insured", "Police-checked cleaners", "Eco-friendly products"];

export const QuoteForm = () => {
  const [form, setForm] = useState(EMPTY);
  const [loading, setLoading] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.phone || !form.service) {
      toast.error("Please fill in your name, email, phone and service.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/quote`, form);
      toast.success("Quote request sent! We'll be in touch within 24 hours.");
      setForm(EMPTY);
    } catch {
      toast.error("Something went wrong. Please call us on " + CONTACT.phone);
    } finally {
      setLoading(false);
    }
  };

  const inputCls = "h-12 rounded-2xl bg-brand-cream border-brand-line text-brand-ink focus-visible:ring-0";

  return (
    <section id="quote" aria-labelledby="quote-heading" className="py-16 md:py-24 bg-brand-surface2">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-[1fr,1.1fr] gap-12 lg:gap-20 items-start">
          <Reveal>
            <div>
              <span className="text-sm font-bold uppercase tracking-wide text-brand-accentink">Free & no-obligation</span>
              <h2 id="quote-heading" className="mt-3 font-heading font-extrabold tracking-tight text-3xl md:text-5xl text-brand-blue leading-[1.1]">Let's get your place sparkling</h2>
              <p className="mt-5 text-lg text-brand-ink2 leading-relaxed max-w-md">Tell us what needs cleaning and we'll send a friendly, transparent quote within 24 hours.</p>

              <ul className="mt-8 grid grid-cols-2 gap-3 max-w-md">
                {PROMISES.map((p) => (
                  <li key={p} className="flex items-center gap-2 text-brand-ink2 text-sm font-semibold"><CheckCircle2 className="h-4 w-4 text-brand-green" aria-hidden="true" /> {p}</li>
                ))}
              </ul>

              <div className="mt-10 space-y-4">
                <a href={CONTACT.phoneHref} data-testid="quote-phone" className="flex items-center gap-4 group text-brand-ink">
                  <span className="grid place-items-center h-11 w-11 rounded-2xl bg-white text-brand-blue border border-brand-line group-hover:bg-brand-blue group-hover:text-white transition-colors duration-300"><Phone className="h-5 w-5" strokeWidth={2} aria-hidden="true" /></span>
                  <span className="text-lg font-bold">{CONTACT.phone}</span>
                </a>
                <a href={`mailto:${CONTACT.email}`} data-testid="quote-email" className="flex items-center gap-4 group text-brand-ink">
                  <span className="grid place-items-center h-11 w-11 rounded-2xl bg-white text-brand-blue border border-brand-line group-hover:bg-brand-blue group-hover:text-white transition-colors duration-300"><Mail className="h-5 w-5" strokeWidth={2} aria-hidden="true" /></span>
                  <span className="text-lg font-bold">{CONTACT.email}</span>
                </a>
                <div className="flex items-center gap-4 text-brand-ink">
                  <span className="grid place-items-center h-11 w-11 rounded-2xl bg-white text-brand-blue border border-brand-line"><MapPin className="h-5 w-5" strokeWidth={2} aria-hidden="true" /></span>
                  <span className="text-lg font-bold">{CONTACT.area}</span>
                </div>
              </div>
            </div>
          </Reveal>

          <Reveal delay={0.1}>
            <motion.form data-testid="quote-form" onSubmit={submit} noValidate className="rounded-3xl bg-white p-7 md:p-10 shadow-[0_30px_70px_-30px_rgba(2,71,157,0.4)] border border-brand-line">
              <div className="grid sm:grid-cols-2 gap-5">
                <div className="space-y-2">
                  <Label htmlFor="q-name">Full name</Label>
                  <Input id="q-name" data-testid="quote-input-name" value={form.name} onChange={set("name")} placeholder="Jane Smith" className={inputCls} />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="q-phone">Phone</Label>
                  <Input id="q-phone" data-testid="quote-phone-input" value={form.phone} onChange={set("phone")} placeholder="0400 000 000" className={inputCls} />
                </div>
              </div>
              <div className="mt-5 space-y-2">
                <Label htmlFor="q-email">Email</Label>
                <Input id="q-email" type="email" data-testid="quote-input-email" value={form.email} onChange={set("email")} placeholder="jane@email.com" className={inputCls} />
              </div>
              <div className="mt-5 space-y-2">
                <Label htmlFor="q-service">Service needed</Label>
                <Select value={form.service} onValueChange={(v) => setForm((f) => ({ ...f, service: v }))}>
                  <SelectTrigger id="q-service" data-testid="quote-service-select" aria-label="Service needed" className="h-12 rounded-2xl bg-brand-cream border-brand-line text-brand-ink"><SelectValue placeholder="Choose a service" /></SelectTrigger>
                  <SelectContent>
                    {SERVICE_OPTIONS.map((s) => (<SelectItem key={s} value={s} data-testid={`quote-service-${s}`}>{s}</SelectItem>))}
                  </SelectContent>
                </Select>
              </div>
              <div className="mt-5 space-y-2">
                <Label htmlFor="q-message">Details (optional)</Label>
                <Textarea id="q-message" data-testid="quote-input-message" value={form.message} onChange={set("message")} placeholder="Bedrooms, size, suburb, preferred date…" rows={4} className="rounded-2xl bg-brand-cream border-brand-line text-brand-ink resize-none focus-visible:ring-0" />
              </div>
              <button type="submit" data-testid="quote-form-submit" disabled={loading} className="mt-7 group w-full flex items-center justify-center gap-2 rounded-full bg-brand-blue text-white text-base font-bold py-4 hover:bg-brand-bluehover transition-colors duration-300 disabled:opacity-70">
                {loading ? <Loader2 className="h-5 w-5 animate-spin" aria-hidden="true" /> : (<>Send my quote request <ArrowRight className="h-5 w-5 transition-transform duration-300 group-hover:translate-x-1" strokeWidth={2} aria-hidden="true" /></>)}
              </button>
            </motion.form>
          </Reveal>
        </div>
      </div>
    </section>
  );
};
